/*
 * File: App.js
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on January 24, 2022 at 9:03 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: October 8, 2024 at 2:40 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useRef
} from 'react';
import {
	Routes, Route, useLocation, useSearchParams
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

// Utilities
import { toastError, toastMessage, toastSuccess } from '../utilities/toaster';
import { scrollPageTo } from '../utilities/position';

// Styles
import { GlobalStyles } from '../styles/global';
import { DarkTheme, LightTheme } from '../styles/colors';

// Slices
import { updateUser, fetchUser } from '../store/slices/user/user.slice';
import { updateLocale, clearLocale } from '../store/slices/locale/locale.slice';
import { updateConsent, initializeConsent } from '../store/slices/consent/consent.slice';
import { updateNotifications } from '../store/slices/notifications/notifications.slice';
import { fetchRewards } from '../store/slices/rewards/rewards.slice';
import { fetchJournalEntries } from '../store/slices/journal/journal.slice';

// Components
import {
	Spinner,
	CookieBar
} from '../components';

// Routes
import { Routes as RoutesCreator } from './Routes';

// Constants
import { UI_MODE_OPTIONS, ALERT_STYLES } from '../../Constants';
import { fetchFunFacts } from '../store/slices/content/content.slice';


/**
 * Constants
 */

const isProduction = process.env.ENV === 'production';
const isEU = false; // For development / hot usage only (does not affect production)


/**
 * Component
 */

const App = (data) => {

	// Set component props from server
	let props = data || {};
	const { isHot } = props;

	// Use hooks
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams({});
	const dispatch = useDispatch();

	// Initialize render count
	const renderCount = useRef(-1);

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);
	const userStatus = useSelector((state) => state.user.status);

	// Get current consent state from hook
	const consentStatus = useSelector((state) => state.consent.status);
	const currentConsent = useSelector((state) => state.consent.currentValue);
	const previousConsent = useSelector((state) => state.consent.previousValue);

	// Get current notification state from hook
	const notificationState = useSelector((state) => state.notifications.value);

	// Get rewards status from hook
	const rewardsStatus = useSelector((state) => state.rewards.status);

	// Get journal status from hook
	const journalEntriesStatus = useSelector((state) => state.journal.entries.status);

	// Get fun fact status
	const funFactsStatus = useSelector((state) => state.content.funfacts.status);

	// Get current UI mode from hook (before client props are initialized)
	let uiMode = useSelector((state) => state.ui.value);
	if (props?.state?.ui) uiMode = props?.state?.ui;

	// Get component state on client
	if (typeof window !== 'undefined') {
		try { props = JSON.parse(decodeURIComponent(window.document.getElementById('data-state').innerHTML)); } catch (e) { }
	}

	// Define current theme
	const currentTheme = uiMode.mode === UI_MODE_OPTIONS.DARK ? DarkTheme : LightTheme;

	// Handle actions on component load
	useEffect(() => {

		// Set locale in state
		if (props.locale && props.locale.localeId) dispatch(updateLocale(props.locale));
		else dispatch(clearLocale());

		// Set consent in state
		if (props?.state?.consent) dispatch(updateConsent(props.state.consent));
		else if (isHot === true) dispatch(initializeConsent(isEU));

		// Check for alert to display
		if (props?.alert) {

			// Get alert parameters
			const { message, style } = props.alert;

			// Show toast
			setTimeout(() => {
				if (style === ALERT_STYLES.INFO) {
					toastMessage(uiMode, message);
				} else if (style === ALERT_STYLES.ERROR) {
					toastError(uiMode, message);
				}
			}, 400);
		}

		// Scroll page to top
		scrollPageTo(0, 1);

	}, []);

	// Handle actions on user update action
	useEffect(() => {

		// Check if user exists
		if (currentUser != null) {

			// Update favicon for unread notification status
			if (currentUser.hasUnreadNotifications === true) {
				document.getElementsByName('favicon-icon').forEach((element) => {
					element.href = `${process.env.CDN_URL}/public/favicon-notification.ico`;
				});
			} else {
				document.getElementsByName('favicon-icon').forEach((element) => {
					element.href = `${process.env.CDN_URL}/public/favicon.ico`;
				});
			}

			// Update notification state
			dispatch(updateNotifications({
				hasUnreadNotifications: currentUser.hasUnreadNotifications,
				unreadNotificationCount: {
					all: currentUser.hasUnreadNotifications
				},
			}));

			// Fetch rewards
			if (currentUser.activeSession === true && !currentUser.isAnonymous && rewardsStatus === 'idle') {
				dispatch(fetchRewards());
			}

			// Fetch journal entries
			if (currentUser.activeSession === true && !currentUser.isAnonymous && currentUser.onboardingComplete && journalEntriesStatus === 'idle') {
				dispatch(fetchJournalEntries());
			}

			// Fetch fun facts
			if (currentUser.activeSession === true && !currentUser.isAnonymous && currentUser.onboardingComplete && funFactsStatus === 'idle') {
				dispatch(fetchFunFacts());
			}
		}
	}, [currentUser]);

	// Handle actions on notification update action
	useEffect(() => {

		// Check if notification exists
		if (notificationState != null) {

			// Update favicon for unread notification status
			if (notificationState.hasUnreadNotifications === true) {
				document.getElementsByName('favicon-icon').forEach((element) => {
					element.href = `${process.env.CDN_URL}/public/favicon-notification.ico`;
				});
			} else {
				document.getElementsByName('favicon-icon').forEach((element) => {
					element.href = `${process.env.CDN_URL}/public/favicon.ico`;
				});
			}
		}
	}, [notificationState]);

	// Handle actions on consent update action
	useEffect(() => {

		// Only update if consent has been set
		if (consentStatus !== 'initial') {

			// Only update if production environment
			if (isProduction) {

				// Check if consent values changed
				const performanceChanged = currentConsent.performanceEnabled !== previousConsent.performanceEnabled;
				const analyticsChanged = currentConsent.analyticsEnabled !== previousConsent.analyticsEnabled;
				const targetingChanged = currentConsent.targetingEnabled !== previousConsent.targetingEnabled;

				// Check if performance consent changed
				if (performanceChanged) {
					if (currentConsent.performanceEnabled === true) {

						// Push GTM trigger
						try {
							window.dataLayer.push({ event: 'consent_given_performance' });
						} catch (err) { }

					} else {

						// Reload page to disable scripts
						window.location.reload(false);

						// Return
						return;
					}
				}

				// Check if analytics consent changed
				if (analyticsChanged) {
					if (currentConsent.analyticsEnabled === true) {

						// Push GTM trigger
						try {
							window.dataLayer.push({ event: 'consent_given_analytics' });
						} catch (err) { }

					} else {

						// Reload page to disable scripts
						window.location.reload(false);

						// Return
						return;
					}
				}

				// Check if targeting consent changed
				if (targetingChanged) {
					if (currentConsent.targetingEnabled === true) {

						// Push GTM trigger
						try {
							window.dataLayer.push({ event: 'consent_given_targeting' });
						} catch (err) { }

					} else {

						// Reload page to disable scripts
						window.location.reload(false);
					}
				}
			}
		}
	}, [currentConsent]);

	// Update render count
	useEffect(() => {
		renderCount.current += 1;
	});

	// Handle actions on route change
	useEffect(() => {

		// Get query parameters
		const queryObj = Object.fromEntries([...searchParams]);

		// Set current user from server props
		const { user } = props;

		// Check if server user exists
		if (user != null && user.activeSession === true) {

			// Get fetched dates
			const serverFetched = user ? new Date(user.lastFetched) : new Date();
			const stateFetched = currentUser ? new Date(currentUser.lastFetched) : serverFetched;

			// Only update state if current user is not newer
			if (stateFetched.getTime() <= serverFetched.getTime()) {

				// Set user from server
				dispatch(updateUser({ ...user, lastFetched: (new Date()).toISOString() }));
			}
		} else if (currentUser == null) {

			// Fetch current user
			dispatch(fetchUser());
		}

		// Check for error query
		if (searchParams.has('error')) {

			// Get error message
			const errorMessage = decodeURIComponent(searchParams.get('error'));

			// Clear error query parameters
			const { error, code, ...additionalParams } = queryObj;
			setSearchParams(additionalParams);

			// Display error toast
			setTimeout(() => {
				toastError(uiMode, errorMessage);
			}, 400);
		}

		// Check for success query
		if (searchParams.has('success')) {

			// Get success message
			const successMessage = decodeURIComponent(searchParams.get('success'));

			// Clear success query parameters
			const { success, ...additionalParams } = queryObj;
			setSearchParams(additionalParams);

			// Display success toast
			setTimeout(() => {
				toastSuccess(uiMode, successMessage);
			}, 400);
		}

		// Handle hash scroll
		const { hash } = location;
		if (!hash) {
			scrollPageTo(0, 0, { behavior: 'smooth' });
		} else {
			const id = hash.replace('#', '');
			const element = document.getElementById(id);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			} else {
				scrollPageTo(0, 0, { behavior: 'smooth' });
			}
		}
	}, [location]);

	// Create routes with props
	const RoutesManifest = RoutesCreator(props, isHot);

	// Set up router render function
	const renderRouter = () => (
		<Routes>
			{RoutesManifest.map((route, index) => {
				const { component: Component } = route;
				return <Route key={route.path || `route-${index}`} path={route.path} element={Component} />;
			})}
		</Routes>
	);

	// Set up component render function
	const renderComponent = () => {
		if (((userStatus === 'idle' || (rewardsStatus === 'idle' && currentUser?.activeSession)) && isHot === true)
			|| userStatus === 'loading'
			|| (rewardsStatus === 'loading' && currentUser?.activeSession)) {
			return <Spinner showMeta />;
		}
		return renderRouter();
	};

	// Render router
	return (
		<ThemeProvider theme={currentTheme}>
			<GlobalStyles />
			{renderComponent()}
			{currentConsent.consentActionTaken !== true && consentStatus !== 'initial' && <CookieBar />}
		</ThemeProvider>
	);
};


/**
 * Configuration
 */

App.propTypes = {
	user: PropTypes.shape(),
	locale: PropTypes.shape(),
	state: PropTypes.shape(),
	alert: PropTypes.shape(),
	isHot: PropTypes.bool
};
App.defaultProps = {
	user: null,
	locale: {},
	state: {},
	alert: {},
	isHot: false
};


/**
 * Exports
 */

export default App;
